import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Search, X, SmilePlus } from "lucide-react";

/* ================================================================
   IconEmojiPicker
   ================================================================
   בוחר אייקונים/אמוג'ים עם חיפוש, קטגוריות וגריד נוח.
   נפתח כ-popover (portal) ליד האלמנט שמפעיל אותו.
   ================================================================ */

/* ── Emoji catalog by category ── */

interface EmojiCategory {
  id: string;
  label: string;
  icon: string;
  emojis: { emoji: string; keywords: string }[];
}

const EMOJI_CATALOG: EmojiCategory[] = [
  {
    id: "popular",
    label: "\u05E4\u05D5\u05E4\u05D5\u05DC\u05E8\u05D9",
    icon: "\u2B50",
    emojis: [
      { emoji: "\u2B50", keywords: "star popular favorite \u05DB\u05D5\u05DB\u05D1 \u05DE\u05D5\u05E2\u05D3\u05E3" },
      { emoji: "\u{1F4CB}", keywords: "clipboard list task \u05DC\u05D5\u05D7 \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA" },
      { emoji: "\u{1F4CC}", keywords: "pin push \u05E0\u05E2\u05E5 \u05E1\u05D9\u05DB\u05D4" },
      { emoji: "\u2705", keywords: "check done complete \u05D5\u05D9 \u05D0\u05D9\u05E9\u05D5\u05E8 \u05D1\u05D5\u05E6\u05E2" },
      { emoji: "\u{1F3AF}", keywords: "target goal bullseye \u05DE\u05D8\u05E8\u05D4 \u05D9\u05E2\u05D3" },
      { emoji: "\u{1F4A1}", keywords: "idea lightbulb think \u05E8\u05E2\u05D9\u05D5\u05DF \u05E0\u05D5\u05E8\u05D4" },
      { emoji: "\u{1F680}", keywords: "rocket launch speed \u05E8\u05E7\u05D8\u05D4 \u05E9\u05D9\u05D2\u05D5\u05E8" },
      { emoji: "\u{1F525}", keywords: "fire hot flame \u05D0\u05E9 \u05D7\u05DD \u05DC\u05D4\u05D1\u05D4" },
      { emoji: "\u{1F4E6}", keywords: "package box delivery \u05D7\u05D1\u05D9\u05DC\u05D4 \u05DE\u05E9\u05DC\u05D5\u05D7" },
      { emoji: "\u{1F4B0}", keywords: "money bag dollar \u05DB\u05E1\u05E3 \u05EA\u05E7\u05E6\u05D9\u05D1" },
      { emoji: "\u{1F48E}", keywords: "gem diamond luxury \u05D9\u05D4\u05DC\u05D5\u05DD \u05E4\u05E8\u05DE\u05D9\u05D5\u05DD" },
      { emoji: "\u{1F3C6}", keywords: "trophy winner award \u05D2\u05D1\u05D9\u05E2 \u05E4\u05E8\u05E1 \u05E0\u05D9\u05E6\u05D7\u05D5\u05DF" },
      { emoji: "\u{1F31F}", keywords: "glowing star quality \u05DB\u05D5\u05DB\u05D1 \u05D6\u05D5\u05D4\u05E8 \u05D0\u05D9\u05DB\u05D5\u05EA" },
      { emoji: "\u{1F4DD}", keywords: "memo write note \u05E4\u05EA\u05E7 \u05DB\u05EA\u05D9\u05D1\u05D4 \u05D4\u05E2\u05E8\u05D4" },
      { emoji: "\u{1F4AC}", keywords: "speech bubble chat comment \u05E9\u05D9\u05D7\u05D4 \u05D3\u05D9\u05D1\u05D5\u05E8 \u05D4\u05E2\u05E8\u05D4" },
      { emoji: "\u260E\uFE0F", keywords: "phone call telephone \u05D8\u05DC\u05E4\u05D5\u05DF \u05E9\u05D9\u05D7\u05D4" },
    ],
  },
  {
    id: "business",
    label: "\u05E2\u05E1\u05E7\u05D9\u05DD",
    icon: "\u{1F4BC}",
    emojis: [
      { emoji: "\u{1F4BC}", keywords: "briefcase business work \u05EA\u05D9\u05E7 \u05E2\u05E1\u05E7\u05D9\u05DD \u05E2\u05D1\u05D5\u05D3\u05D4" },
      { emoji: "\u{1F4CA}", keywords: "chart graph analytics \u05D2\u05E8\u05E3 \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05D0\u05E0\u05DC\u05D9\u05D8\u05D9\u05E7\u05D4" },
      { emoji: "\u{1F4C8}", keywords: "chart up growth rising \u05E2\u05DC\u05D9\u05D4 \u05E6\u05DE\u05D9\u05D7\u05D4" },
      { emoji: "\u{1F4C9}", keywords: "chart down decline \u05D9\u05E8\u05D9\u05D3\u05D4" },
      { emoji: "\u{1F4B3}", keywords: "credit card payment \u05DB\u05E8\u05D8\u05D9\u05E1 \u05D0\u05E9\u05E8\u05D0\u05D9 \u05EA\u05E9\u05DC\u05D5\u05DD" },
      { emoji: "\u{1F4B5}", keywords: "dollar money cash \u05DB\u05E1\u05E3 \u05D3\u05D5\u05DC\u05E8 \u05DE\u05D6\u05D5\u05DE\u05DF" },
      { emoji: "\u{1F3E2}", keywords: "building office corporate \u05DE\u05E9\u05E8\u05D3 \u05D1\u05E0\u05D9\u05D9\u05DF" },
      { emoji: "\u{1F3E0}", keywords: "house home \u05D1\u05D9\u05EA \u05D3\u05D9\u05E8\u05D4" },
      { emoji: "\u{1F465}", keywords: "people team group \u05E6\u05D5\u05D5\u05EA \u05D0\u05E0\u05E9\u05D9\u05DD \u05E7\u05D1\u05D5\u05E6\u05D4" },
      { emoji: "\u{1F464}", keywords: "person user profile \u05D0\u05D3\u05DD \u05DE\u05E9\u05EA\u05DE\u05E9 \u05DC\u05E7\u05D5\u05D7" },
      { emoji: "\u{1F4C5}", keywords: "calendar date schedule \u05DC\u05D5\u05D7 \u05E9\u05E0\u05D4 \u05EA\u05D0\u05E8\u05D9\u05DA" },
      { emoji: "\u{1F4C4}", keywords: "document page file \u05DE\u05E1\u05DE\u05DA \u05E7\u05D5\u05D1\u05E5 \u05D3\u05E3" },
      { emoji: "\u{1F4CE}", keywords: "paperclip attachment \u05DE\u05D4\u05D3\u05E7 \u05E7\u05D5\u05D1\u05E5 \u05DE\u05E6\u05D5\u05E8\u05E3" },
      { emoji: "\u{1F4E9}", keywords: "email inbox message \u05DE\u05D9\u05D9\u05DC \u05D3\u05D5\u05D0\u05E8 \u05D4\u05D5\u05D3\u05E2\u05D4" },
      { emoji: "\u{1F4DE}", keywords: "telephone call receiver \u05D8\u05DC\u05E4\u05D5\u05DF \u05E9\u05D9\u05D7\u05D4 \u05E7\u05E9\u05E8" },
      { emoji: "\u{1F4BB}", keywords: "laptop computer \u05DE\u05D7\u05E9\u05D1 \u05E0\u05D9\u05D9\u05D3" },
    ],
  },
  {
    id: "tech",
    label: "\u05D8\u05DB\u05E0\u05D5\u05DC\u05D5\u05D2\u05D9\u05D4",
    icon: "\u{1F4BB}",
    emojis: [
      { emoji: "\u{1F5A5}\uFE0F", keywords: "desktop monitor screen \u05DE\u05E1\u05DA \u05DE\u05D7\u05E9\u05D1" },
      { emoji: "\u{1F4F1}", keywords: "mobile phone smartphone \u05E0\u05D9\u05D9\u05D3 \u05D8\u05DC\u05E4\u05D5\u05DF" },
      { emoji: "\u2699\uFE0F", keywords: "gear settings config \u05D2\u05DC\u05D2\u05DC \u05D4\u05D2\u05D3\u05E8\u05D5\u05EA" },
      { emoji: "\u{1F50D}", keywords: "search magnify find \u05D7\u05D9\u05E4\u05D5\u05E9 \u05D6\u05DB\u05D5\u05DB\u05D9\u05EA" },
      { emoji: "\u{1F512}", keywords: "lock security privacy \u05DE\u05E0\u05E2\u05D5\u05DC \u05D0\u05D1\u05D8\u05D7\u05D4" },
      { emoji: "\u{1F513}", keywords: "unlock open access \u05E4\u05EA\u05D5\u05D7 \u05D2\u05D9\u05E9\u05D4" },
      { emoji: "\u{1F6E0}\uFE0F", keywords: "tools wrench build \u05DB\u05DC\u05D9\u05DD \u05D1\u05E0\u05D9\u05D9\u05D4" },
      { emoji: "\u{1F50C}", keywords: "plug electric power \u05EA\u05E7\u05E2 \u05D7\u05E9\u05DE\u05DC" },
      { emoji: "\u{1F4E1}", keywords: "satellite antenna signal \u05DC\u05D5\u05D9\u05D9\u05DF \u05D0\u05E0\u05D8\u05E0\u05D4" },
      { emoji: "\u{1F4F6}", keywords: "signal wifi wireless \u05D0\u05D5\u05EA \u05D5\u05D9\u05D9\u05E4\u05D9" },
      { emoji: "\u26A1", keywords: "lightning bolt energy \u05D1\u05E8\u05E7 \u05D0\u05E0\u05E8\u05D2\u05D9\u05D4 \u05DE\u05D4\u05D9\u05E8" },
      { emoji: "\u{1F310}", keywords: "globe web internet world \u05E2\u05D5\u05DC\u05DD \u05D0\u05D9\u05E0\u05D8\u05E8\u05E0\u05D8 \u05D0\u05EA\u05E8" },
      { emoji: "\u{1F916}", keywords: "robot ai automation \u05E8\u05D5\u05D1\u05D5\u05D8 \u05D1\u05D9\u05E0\u05D4 \u05DE\u05DC\u05D0\u05DB\u05D5\u05EA\u05D9\u05EA" },
      { emoji: "\u{1F9E0}", keywords: "brain smart ai \u05DE\u05D5\u05D7 \u05D7\u05DB\u05DD \u05D1\u05D9\u05E0\u05D4" },
      { emoji: "\u{1F4E0}", keywords: "fax machine \u05E4\u05E7\u05E1 \u05DE\u05DB\u05D5\u05E0\u05D4" },
      { emoji: "\u{1F4BE}", keywords: "disk save storage \u05E9\u05DE\u05D9\u05E8\u05D4 \u05D0\u05D7\u05E1\u05D5\u05DF" },
    ],
  },
  {
    id: "hands",
    label: "\u05D9\u05D3\u05D9\u05D9\u05DD",
    icon: "\u{1F91D}",
    emojis: [
      { emoji: "\u{1F44D}", keywords: "thumbs up like good \u05DC\u05D0\u05D9\u05E7 \u05D8\u05D5\u05D1 \u05D0\u05D2\u05D5\u05D3\u05DC" },
      { emoji: "\u{1F44E}", keywords: "thumbs down dislike bad \u05DC\u05D0 \u05D8\u05D5\u05D1 \u05D2\u05E8\u05D5\u05E2" },
      { emoji: "\u{1F91D}", keywords: "handshake deal agree \u05DC\u05D7\u05D9\u05E6\u05EA \u05D9\u05D3 \u05E2\u05E1\u05E7\u05D4" },
      { emoji: "\u{1F64F}", keywords: "pray please thanks \u05EA\u05E4\u05D9\u05DC\u05D4 \u05EA\u05D5\u05D3\u05D4 \u05D1\u05E7\u05E9\u05D4" },
      { emoji: "\u{1F44B}", keywords: "wave hello hi \u05E9\u05DC\u05D5\u05DD \u05D4\u05D9\u05D9 \u05E0\u05E4\u05E0\u05D5\u05E3" },
      { emoji: "\u{1F44F}", keywords: "clap bravo applaud \u05DE\u05D7\u05D9\u05D0\u05D5\u05EA \u05DB\u05E4\u05D9\u05D9\u05DD" },
      { emoji: "\u270C\uFE0F", keywords: "peace victory \u05E9\u05DC\u05D5\u05DD \u05E0\u05D9\u05E6\u05D7\u05D5\u05DF" },
      { emoji: "\u{1F4AA}", keywords: "muscle strong power \u05E9\u05E8\u05D9\u05E8 \u05D7\u05D6\u05E7 \u05DB\u05D5\u05D7" },
    ],
  },
  {
    id: "nature",
    label: "\u05D8\u05D1\u05E2",
    icon: "\u{1F33F}",
    emojis: [
      { emoji: "\u{1F33F}", keywords: "herb leaf green nature \u05D8\u05D1\u05E2 \u05E2\u05DC\u05D4 \u05D9\u05E8\u05D5\u05E7" },
      { emoji: "\u{1F33B}", keywords: "sunflower yellow flower \u05D7\u05DE\u05E0\u05D9\u05EA \u05E4\u05E8\u05D7" },
      { emoji: "\u{1F333}", keywords: "tree forest nature \u05E2\u05E5 \u05D9\u05E2\u05E8" },
      { emoji: "\u{1F30A}", keywords: "wave ocean sea water \u05D2\u05DC \u05D9\u05DD \u05DE\u05D9\u05DD" },
      { emoji: "\u2600\uFE0F", keywords: "sun sunny bright \u05E9\u05DE\u05E9 \u05D7\u05DD \u05D1\u05D4\u05D9\u05E8" },
      { emoji: "\u{1F308}", keywords: "rainbow colorful \u05E7\u05E9\u05EA \u05E6\u05D1\u05E2\u05D5\u05E0\u05D9" },
      { emoji: "\u{1F48E}", keywords: "gem diamond precious \u05D9\u05D4\u05DC\u05D5\u05DD \u05D9\u05E7\u05E8" },
      { emoji: "\u{1F30D}", keywords: "earth globe world \u05DB\u05D3\u05D5\u05E8 \u05D4\u05D0\u05E8\u05E5 \u05E2\u05D5\u05DC\u05DD" },
    ],
  },
  {
    id: "emotions",
    label: "\u05E4\u05E8\u05E6\u05D5\u05E4\u05D9\u05DD",
    icon: "\u{1F600}",
    emojis: [
      { emoji: "\u{1F600}", keywords: "smile happy grin \u05D7\u05D9\u05D5\u05DA \u05E9\u05DE\u05D7\u05D4" },
      { emoji: "\u{1F60D}", keywords: "love heart eyes \u05D0\u05D4\u05D1\u05D4 \u05DC\u05D1\u05D1\u05D5\u05EA" },
      { emoji: "\u{1F60E}", keywords: "cool sunglasses \u05DE\u05D2\u05E0\u05D9\u05D1 \u05DE\u05E9\u05E7\u05E4\u05D9\u05D9\u05DD" },
      { emoji: "\u{1F914}", keywords: "thinking hmm consider \u05D7\u05D5\u05E9\u05D1 \u05DE\u05D7\u05E9\u05D1\u05D4" },
      { emoji: "\u{1F929}", keywords: "star struck amazed wow \u05DE\u05D5\u05E4\u05EA\u05E2 \u05D5\u05D0\u05D5" },
      { emoji: "\u{1F389}", keywords: "party celebrate \u05DE\u05E1\u05D9\u05D1\u05D4 \u05D7\u05D2\u05D9\u05D2\u05D4" },
      { emoji: "\u{1F381}", keywords: "gift present surprise \u05DE\u05EA\u05E0\u05D4 \u05D4\u05E4\u05EA\u05E2\u05D4" },
      { emoji: "\u2764\uFE0F", keywords: "heart love red \u05DC\u05D1 \u05D0\u05D4\u05D1\u05D4 \u05D0\u05D3\u05D5\u05DD" },
    ],
  },
  {
    id: "food",
    label: "\u05D0\u05D5\u05DB\u05DC",
    icon: "\u{1F374}",
    emojis: [
      { emoji: "\u{1F374}", keywords: "fork knife food eat \u05D0\u05D5\u05DB\u05DC \u05DE\u05E1\u05E2\u05D3\u05D4" },
      { emoji: "\u2615", keywords: "coffee cup hot drink \u05E7\u05E4\u05D4 \u05DE\u05E9\u05E7\u05D4 \u05D7\u05DD" },
      { emoji: "\u{1F370}", keywords: "cake sweet dessert \u05E2\u05D5\u05D2\u05D4 \u05DE\u05EA\u05D5\u05E7" },
      { emoji: "\u{1F355}", keywords: "pizza slice food \u05E4\u05D9\u05E6\u05D4 \u05D0\u05D5\u05DB\u05DC" },
      { emoji: "\u{1F957}", keywords: "salad healthy green \u05E1\u05DC\u05D8 \u05D1\u05E8\u05D9\u05D0" },
      { emoji: "\u{1F37D}\uFE0F", keywords: "plate fork knife dining \u05E6\u05DC\u05D7\u05EA \u05D0\u05D5\u05DB\u05DC" },
      { emoji: "\u{1F377}", keywords: "wine glass drink \u05D9\u05D9\u05DF \u05DB\u05D5\u05E1" },
      { emoji: "\u{1F382}", keywords: "birthday cake candles \u05D9\u05D5\u05DD \u05D4\u05D5\u05DC\u05D3\u05EA \u05E2\u05D5\u05D2\u05D4" },
    ],
  },
  {
    id: "transport",
    label: "\u05EA\u05D7\u05D1\u05D5\u05E8\u05D4",
    icon: "\u{1F697}",
    emojis: [
      { emoji: "\u{1F697}", keywords: "car auto vehicle \u05E8\u05DB\u05D1 \u05DE\u05DB\u05D5\u05E0\u05D9\u05EA" },
      { emoji: "\u{1F6E9}\uFE0F", keywords: "airplane plane fly \u05DE\u05D8\u05D5\u05E1 \u05D8\u05D9\u05E1\u05D4" },
      { emoji: "\u{1F6A2}", keywords: "ship boat cruise \u05E1\u05E4\u05D9\u05E0\u05D4 \u05D0\u05D5\u05E0\u05D9\u05D4" },
      { emoji: "\u{1F3D7}\uFE0F", keywords: "construction crane building \u05D1\u05E0\u05D9\u05D9\u05D4 \u05E2\u05D2\u05D5\u05E8\u05DF" },
      { emoji: "\u{1F69A}", keywords: "truck delivery transport \u05DE\u05E9\u05D0\u05D9\u05EA \u05D4\u05D5\u05D1\u05DC\u05D4" },
      { emoji: "\u{1F6F5}", keywords: "scooter motorcycle \u05E7\u05D8\u05E0\u05D5\u05E2 \u05D0\u05D5\u05E4\u05E0\u05D5\u05E2" },
      { emoji: "\u{1F3E1}", keywords: "house garden home \u05D1\u05D9\u05EA \u05D2\u05E0\u05D4" },
      { emoji: "\u{1F3EB}", keywords: "school education \u05D1\u05D9\u05EA \u05E1\u05E4\u05E8 \u05DC\u05D9\u05DE\u05D5\u05D3\u05D9\u05DD" },
    ],
  },
  {
    id: "symbols",
    label: "\u05E1\u05DE\u05DC\u05D9\u05DD",
    icon: "\u2714\uFE0F",
    emojis: [
      { emoji: "\u2714\uFE0F", keywords: "check mark done \u05D5\u05D9 \u05E1\u05D9\u05DE\u05D5\u05DF" },
      { emoji: "\u274C", keywords: "cross wrong error \u05E9\u05D2\u05D9\u05D0\u05D4 \u05D0\u05D9\u05E7\u05E1" },
      { emoji: "\u2757", keywords: "exclamation warning alert \u05E7\u05E8\u05D9\u05D0\u05D4 \u05D0\u05D6\u05D4\u05E8\u05D4" },
      { emoji: "\u2753", keywords: "question ask unknown \u05E9\u05D0\u05DC\u05D4 \u05E1\u05D9\u05DE\u05DF" },
      { emoji: "\u{1F4A2}", keywords: "anger symbol \u05DB\u05E2\u05E1" },
      { emoji: "\u267B\uFE0F", keywords: "recycle green eco \u05DE\u05D9\u05D7\u05D6\u05D5\u05E8 \u05D0\u05E7\u05D5\u05DC\u05D5\u05D2\u05D9" },
      { emoji: "\u{1F6AB}", keywords: "prohibited forbidden no \u05D0\u05E1\u05D5\u05E8" },
      { emoji: "\u{1F4A4}", keywords: "sleep zzz rest \u05E9\u05D9\u05E0\u05D4 \u05DE\u05E0\u05D5\u05D7\u05D4" },
      { emoji: "\u{1F504}", keywords: "refresh cycle repeat \u05E8\u05E2\u05E0\u05D5\u05DF \u05D7\u05D6\u05E8\u05D4" },
      { emoji: "\u{1F519}", keywords: "back return \u05D7\u05D6\u05D5\u05E8 \u05D0\u05D7\u05D5\u05E8\u05D4" },
      { emoji: "\u{1F51D}", keywords: "top up arrow \u05DC\u05DE\u05E2\u05DC\u05D4" },
      { emoji: "\u{1F6A8}", keywords: "emergency alarm alert \u05D7\u05D9\u05E8\u05D5\u05DD \u05D4\u05EA\u05E8\u05D0\u05D4" },
      { emoji: "\u{1F194}", keywords: "id badge identity \u05D6\u05D9\u05D4\u05D5\u05D9 \u05EA\u05E2\u05D5\u05D3\u05D4" },
      { emoji: "\u{1F195}", keywords: "new fresh \u05D7\u05D3\u05E9" },
      { emoji: "\u{1F198}", keywords: "sos help emergency \u05E2\u05D6\u05E8\u05D4" },
      { emoji: "\u{1F4AF}", keywords: "100 perfect score \u05DE\u05D0\u05D4 \u05DE\u05D5\u05E9\u05DC\u05DD" },
    ],
  },
  {
    id: "creative",
    label: "\u05D9\u05E6\u05D9\u05E8\u05EA\u05D9\u05D5\u05EA",
    icon: "\u{1F3A8}",
    emojis: [
      { emoji: "\u{1F3A8}", keywords: "art paint palette creative \u05D0\u05DE\u05E0\u05D5\u05EA \u05E6\u05D9\u05D5\u05E8 \u05E2\u05D9\u05E6\u05D5\u05D1" },
      { emoji: "\u{1F3AC}", keywords: "movie film cinema \u05E1\u05E8\u05D8 \u05E7\u05D5\u05DC\u05E0\u05D5\u05E2" },
      { emoji: "\u{1F3B5}", keywords: "music note song \u05DE\u05D5\u05D6\u05D9\u05E7\u05D4 \u05EA\u05D5 \u05E9\u05D9\u05E8" },
      { emoji: "\u{1F3B6}", keywords: "music notes melody \u05DE\u05D5\u05D6\u05D9\u05E7\u05D4 \u05DE\u05DC\u05D5\u05D3\u05D9\u05D4" },
      { emoji: "\u{1F4F7}", keywords: "camera photo picture \u05DE\u05E6\u05DC\u05DE\u05D4 \u05EA\u05DE\u05D5\u05E0\u05D4 \u05E6\u05D9\u05DC\u05D5\u05DD" },
      { emoji: "\u{1F4F9}", keywords: "video camera film \u05DE\u05E6\u05DC\u05DE\u05EA \u05D5\u05D9\u05D3\u05D0\u05D5" },
      { emoji: "\u{1F3AD}", keywords: "theater masks drama \u05EA\u05D9\u05D0\u05D8\u05E8\u05D5\u05DF \u05DE\u05E1\u05DB\u05D5\u05EA" },
      { emoji: "\u{1F58C}\uFE0F", keywords: "paintbrush art draw \u05DE\u05DB\u05D7\u05D5\u05DC \u05E6\u05D9\u05D5\u05E8" },
      { emoji: "\u270D\uFE0F", keywords: "writing hand pen \u05DB\u05EA\u05D9\u05D1\u05D4 \u05D9\u05D3" },
      { emoji: "\u{1F4D6}", keywords: "book open read \u05E1\u05E4\u05E8 \u05E4\u05EA\u05D5\u05D7 \u05E7\u05E8\u05D9\u05D0\u05D4" },
      { emoji: "\u{1F4DA}", keywords: "books library study \u05E1\u05E4\u05E8\u05D9\u05DD \u05E1\u05E4\u05E8\u05D9\u05D9\u05D4 \u05DC\u05D9\u05DE\u05D5\u05D3" },
      { emoji: "\u{1F4D0}", keywords: "ruler measure design \u05E1\u05E8\u05D2\u05DC \u05DE\u05D3\u05D9\u05D3\u05D4 \u05E2\u05D9\u05E6\u05D5\u05D1" },
    ],
  },
  {
    id: "health",
    label: "\u05D1\u05E8\u05D9\u05D0\u05D5\u05EA",
    icon: "\u{1F3E5}",
    emojis: [
      { emoji: "\u{1F3E5}", keywords: "hospital health medical \u05D1\u05D9\u05EA \u05D7\u05D5\u05DC\u05D9\u05DD \u05E8\u05E4\u05D5\u05D0\u05D4" },
      { emoji: "\u{1FA7A}", keywords: "stethoscope doctor medical \u05E1\u05D8\u05D8\u05D5\u05E1\u05E7\u05D5\u05E4 \u05E8\u05D5\u05E4\u05D0" },
      { emoji: "\u{1F489}", keywords: "syringe vaccine injection \u05D6\u05E8\u05D9\u05E7\u05D4 \u05D7\u05D9\u05E1\u05D5\u05DF" },
      { emoji: "\u{1F48A}", keywords: "pill medicine drug \u05D2\u05DC\u05D5\u05DC\u05D4 \u05EA\u05E8\u05D5\u05E4\u05D4" },
      { emoji: "\u{1F9D8}", keywords: "meditation yoga zen \u05DE\u05D3\u05D9\u05D8\u05E6\u05D9\u05D4 \u05D9\u05D5\u05D2\u05D4" },
      { emoji: "\u{1F6B4}", keywords: "bicycle cycling exercise sport \u05D0\u05D5\u05E4\u05E0\u05D9\u05D9\u05DD \u05E1\u05E4\u05D5\u05E8\u05D8" },
      { emoji: "\u{1F9E1}", keywords: "orange heart care \u05DC\u05D1 \u05D0\u05DB\u05E4\u05EA" },
      { emoji: "\u{1F9D1}\u200D\u2695\uFE0F", keywords: "doctor physician health \u05E8\u05D5\u05E4\u05D0 \u05E8\u05E4\u05D5\u05D0\u05D4" },
    ],
  },
  {
    id: "flags",
    label: "\u05D3\u05D2\u05DC\u05D9\u05DD",
    icon: "\u{1F6A9}",
    emojis: [
      { emoji: "\u{1F6A9}", keywords: "flag marker \u05D3\u05D2\u05DC \u05E1\u05DE\u05DF" },
      { emoji: "\u{1F3C1}", keywords: "checkered flag finish race \u05E1\u05D9\u05D5\u05DD \u05DE\u05E8\u05D5\u05E5" },
      { emoji: "\u{1F1EE}\u{1F1F1}", keywords: "israel flag \u05D3\u05D2\u05DC \u05D9\u05E9\u05E8\u05D0\u05DC" },
      { emoji: "\u{1F1FA}\u{1F1F8}", keywords: "usa flag america \u05D3\u05D2\u05DC \u05D0\u05E8\u05D4\u05D1" },
      { emoji: "\u{1F3F4}", keywords: "black flag pirate \u05D3\u05D2\u05DC \u05E9\u05D7\u05D5\u05E8" },
      { emoji: "\u{1F3F3}\uFE0F", keywords: "white flag surrender \u05D3\u05D2\u05DC \u05DC\u05D1\u05DF" },
    ],
  },
];

/* ── Flatten all emojis for search ── */
const ALL_EMOJIS = EMOJI_CATALOG.flatMap((cat) =>
  cat.emojis.map((e) => ({ ...e, category: cat.id }))
);

/* ================================================================
   IconEmojiPicker Component
   ================================================================ */

interface IconEmojiPickerProps {
  /** Currently selected value */
  value: string;
  /** Callback when an emoji is selected */
  onChange: (emoji: string) => void;
  /** Whether the picker is open */
  open: boolean;
  /** Close the picker */
  onClose: () => void;
  /** Anchor element for positioning */
  anchorEl: HTMLElement | null;
}

export function IconEmojiPicker({ value, onChange, open, onClose, anchorEl }: IconEmojiPickerProps) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("popular");
  const searchRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Position
  useEffect(() => {
    if (!open || !anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const panelW = 340;
    const panelH = 420;

    let top = rect.bottom + 6;
    if (top + panelH > window.innerHeight - 8) {
      top = Math.max(8, rect.top - panelH - 6);
    }

    let left = rect.left;
    if (left + panelW > window.innerWidth - 8) {
      left = window.innerWidth - panelW - 8;
    }
    if (left < 8) left = 8;

    setPos({ top, left });
  }, [open, anchorEl]);

  // Auto-focus search
  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return ALL_EMOJIS.filter((e) => e.keywords.toLowerCase().includes(q) || e.emoji.includes(q));
  }, [search]);

  const handleSelect = useCallback(
    (emoji: string) => {
      onChange(emoji);
      onClose();
    },
    [onChange, onClose]
  );

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[1999]" onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed z-[2000] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
        style={{ top: pos.top, left: pos.left, width: 340, maxHeight: 420 }}
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100" style={{ background: "linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)" }}>
          <SmilePlus className="w-4 h-4 text-blue-500" />
          <span className="text-xs text-gray-700 flex-1" style={{ fontWeight: 700 }}>
            {"\u05D1\u05D7\u05D9\u05E8\u05EA \u05D0\u05D9\u05D9\u05E7\u05D5\u05DF"}
          </span>
          {value && (
            <span className="text-lg leading-none">{value}</span>
          )}
          <button
            type="button"
            className="btnReset w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            onClick={onClose}
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={"\u05D7\u05D9\u05E4\u05D5\u05E9 \u05D0\u05D9\u05D9\u05E7\u05D5\u05DF..."}
              className="w-full pr-8 pl-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-gray-50"
              dir="rtl"
            />
            {search && (
              <button
                type="button"
                className="btnReset absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
                onClick={() => setSearch("")}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Category tabs (hidden when searching) */}
        {!filtered && (
          <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 overflow-x-auto scrollbar-thin">
            {EMOJI_CATALOG.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={`btnReset px-2 py-1 rounded-md text-xs whitespace-nowrap flex items-center gap-1 ${
                  activeCategory === cat.id
                    ? "text-blue-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                style={{
                  fontWeight: activeCategory === cat.id ? 600 : 400,
                  backgroundColor: activeCategory === cat.id ? "#eff6ff" : "transparent",
                }}
                onClick={() => setActiveCategory(cat.id)}
                title={cat.label}
              >
                <span className="text-sm">{cat.icon}</span>
              </button>
            ))}
          </div>
        )}

        {/* Emoji grid */}
        <div className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
          {filtered ? (
            /* Search results */
            filtered.length > 0 ? (
              <div>
                <p className="text-[10px] text-gray-400 mb-1.5" style={{ fontWeight: 500 }}>
                  {filtered.length} {"\u05EA\u05D5\u05E6\u05D0\u05D5\u05EA"}
                </p>
                <div className="grid grid-cols-8 gap-1">
                  {filtered.map((item, idx) => (
                    <button
                      key={`${item.emoji}-${idx}`}
                      type="button"
                      className={`btnReset w-8 h-8 flex items-center justify-center text-xl rounded-lg hover:bg-blue-50 ${
                        value === item.emoji ? "ring-2 ring-blue-400 ring-offset-1" : ""
                      }`}
                      style={{ backgroundColor: value === item.emoji ? "#eff6ff" : undefined }}
                      onClick={() => handleSelect(item.emoji)}
                      title={item.keywords.split(" ").slice(0, 3).join(" ")}
                    >
                      {item.emoji}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{"\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA"}</p>
                <p className="text-xs mt-1 text-gray-300">{"\u05E0\u05E1\u05D5 \u05DE\u05D9\u05DC\u05EA \u05D7\u05D9\u05E4\u05D5\u05E9 \u05D0\u05D7\u05E8\u05EA"}</p>
              </div>
            )
          ) : (
            /* Category view */
            (() => {
              const cat = EMOJI_CATALOG.find((c) => c.id === activeCategory) || EMOJI_CATALOG[0];
              return (
                <div>
                  <p className="text-[10px] text-gray-400 mb-2 flex items-center gap-1" style={{ fontWeight: 600 }}>
                    <span className="text-sm">{cat.icon}</span>
                    {cat.label}
                  </p>
                  <div className="grid grid-cols-8 gap-1">
                    {cat.emojis.map((item, idx) => (
                      <button
                        key={`${item.emoji}-${idx}`}
                        type="button"
                        className={`btnReset w-8 h-8 flex items-center justify-center text-xl rounded-lg hover:bg-blue-50 ${
                          value === item.emoji ? "ring-2 ring-blue-400 ring-offset-1" : ""
                        }`}
                        style={{ backgroundColor: value === item.emoji ? "#eff6ff" : undefined }}
                        onClick={() => handleSelect(item.emoji)}
                        title={item.keywords.split(" ").slice(0, 3).join(" ")}
                      >
                        {item.emoji}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()
          )}
        </div>

        {/* Footer: custom emoji input */}
        <div className="border-t border-gray-100 px-3 py-2 flex items-center gap-2">
          <span className="text-[10px] text-gray-400 shrink-0">{"\u05D4\u05E7\u05DC\u05D3\u05D4 \u05D9\u05D3\u05E0\u05D9\u05EA:"}</span>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 text-center"
            dir="ltr"
            maxLength={4}
            placeholder={"\u{1F4CB}"}
          />
        </div>
      </div>
    </>,
    document.body
  );
}

/* ================================================================
   IconEmojiField — compact input with picker trigger button
   ================================================================
   שדה אייקון שמכיל כפתור לפתיחת הבוחר.
   ================================================================ */

interface IconEmojiFieldProps {
  value: string;
  onChange: (emoji: string) => void;
  label?: string;
  className?: string;
}

export function IconEmojiField({ value, onChange, label, className = "" }: IconEmojiFieldProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={className}>
      {label && <label className="text-xs text-gray-500 mb-1 block">{label}</label>}
      <div
        ref={triggerRef}
        className="relative flex items-center border border-gray-300 rounded-lg overflow-hidden hover:border-blue-400 transition-colors cursor-pointer"
        onClick={() => setOpen(true)}
      >
        {/* Current emoji display */}
        <div className="flex-1 px-3 py-2 text-sm text-center min-h-[38px] flex items-center justify-center">
          {value ? (
            <span className="text-2xl leading-none">{value}</span>
          ) : (
            <span className="text-gray-300 text-sm">{"\u05DC\u05D7\u05E6\u05D5 \u05DC\u05D1\u05D7\u05D9\u05E8\u05D4..."}</span>
          )}
        </div>
        {/* Picker trigger */}
        <button
          type="button"
          className="btnReset h-full px-2.5 border-r border-gray-200 flex items-center justify-center text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
          style={{ backgroundColor: "transparent" }}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          title={"\u05E4\u05EA\u05D7 \u05DE\u05D0\u05D2\u05E8 \u05D0\u05D9\u05D9\u05E7\u05D5\u05E0\u05D9\u05DD"}
        >
          <SmilePlus className="w-4 h-4" />
        </button>
      </div>
      <IconEmojiPicker
        value={value}
        onChange={onChange}
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={triggerRef.current}
      />
    </div>
  );
}
